'use client'

import * as React from 'react'
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { FOCUSABLE_SELECTOR } from '@/lib/focus'
import { Button } from '@/components/ui/button'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
  settledSlidesInView: number[] | null
  totalSlides: number
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

// Unlike CarouselContext (which throws when used outside a provider), this context
// uses undefined as a safe default — CarouselItem gracefully handles missing index
// when rendered outside CarouselContent.
const CarouselItemIndexContext = React.createContext<number | undefined>(undefined)

function useCarouselItemIndex() {
  return React.useContext(CarouselItemIndexContext)
}

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  // Read direction from document (managed centrally by useRtl hook in App.tsx)
  const direction = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr'

  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
      direction,
      inViewThreshold: opts?.inViewThreshold ?? 0.5,
      // watchFocus is hardcoded off: we manage focus explicitly after user navigation
      // (see shouldFocusViewportRef). Allowing callers to override this via opts would
      // conflict with our focus management logic.
      watchFocus: false,
    },
    plugins
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)
  const [settledSlidesInView, setSettledSlidesInView] = React.useState<number[] | null>(null)
  const [totalSlides, setTotalSlides] = React.useState(0)
  const userNavigatedRef = React.useRef(false)
  const shouldFocusViewportRef = React.useRef(false)

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
    // Also update visible slides on select so instant scrolls (scrollTo(x, true))
    // immediately apply inert to off-screen slides without waiting for settle.
    const visible = api.slidesInView()
    if (visible.length > 0) {
      setSettledSlidesInView(visible)
    }
  }, [])

  const scrollPrev = React.useCallback(() => {
    userNavigatedRef.current = true
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    userNavigatedRef.current = true
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement
      const slot = target.getAttribute?.('data-slot')
      // Only handle arrows when focus is on nav buttons or viewport
      if (slot !== 'carousel-previous' && slot !== 'carousel-next' && slot !== 'carousel-viewport') {
        return
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        direction === 'rtl' ? scrollNext() : scrollPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        direction === 'rtl' ? scrollPrev() : scrollNext()
      }
    },
    [direction, scrollPrev, scrollNext]
  )

  React.useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  const onSettle = React.useCallback((api: CarouselApi) => {
    if (!api) return
    const visible = api.slidesInView()
    // Guard against empty: if no slides are reported in view (e.g. during
    // Dialog entrance animation when the container isn't fully sized),
    // skip the update to avoid marking ALL slides as inert.
    if (visible.length === 0) return
    setSettledSlidesInView(visible)

    // If user initiated navigation, defer focus until after React re-renders
    // with the updated aria-label (setSettledSlidesInView triggers re-render)
    if (userNavigatedRef.current) {
      userNavigatedRef.current = false
      shouldFocusViewportRef.current = true
    }

    // Rescue focus if the active element is inside a slide that just became inert
    queueMicrotask(() => {
      const active = document.activeElement
      if (!active || active === document.body) return

      // Only rescue if focus is inside the carousel content area (not nav buttons)
      const carouselContent = api.rootNode()
      if (!carouselContent?.contains(active)) return

      // Check if the focused element is inside a non-visible slide
      const slideEl = active.closest('[data-slot="carousel-item"]')
      if (!slideEl) return

      const slideIndex = Number(slideEl.getAttribute('data-slide-index'))
      if (Number.isNaN(slideIndex) || visible.includes(slideIndex)) return

      // Focus the first focusable element in the first visible slide
      const allSlides = carouselContent.querySelectorAll('[data-slot="carousel-item"]')
      for (const idx of visible) {
        const visibleSlide = allSlides[idx]
        if (!visibleSlide) continue
        const focusable = visibleSlide.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
        if (focusable) {
          focusable.focus()
          return
        }
      }
    })
  }, [])

  React.useEffect(() => {
    if (!api) return
    onSelect(api)

    // Initialize slides in view and total slides
    setTotalSlides(api.slideNodes().length)
    const initialVisible = api.slidesInView()
    if (initialVisible.length > 0) {
      setSettledSlidesInView(initialVisible)
    }

    const onReInit = (api: CarouselApi) => {
      onSelect(api)
      if (api) {
        setTotalSlides(api.slideNodes().length)
        const reInitVisible = api.slidesInView()
        if (reInitVisible.length > 0) {
          setSettledSlidesInView(reInitVisible)
        }
      }
    }

    const onSlidesInView = (api: CarouselApi) => {
      if (!api) return
      const visible = api.slidesInView()
      if (visible.length > 0) {
        // Only use as init fallback — settle handles ongoing updates
        setSettledSlidesInView(prev => prev !== null ? prev : visible)
      }
    }

    api.on('reInit', onReInit)
    api.on('select', onSelect)
    api.on('settle', onSettle)
    api.on('slidesInView', onSlidesInView)

    return () => {
      api?.off('select', onSelect)
      api?.off('reInit', onReInit)
      api?.off('settle', onSettle)
      api?.off('slidesInView', onSlidesInView)
    }
  }, [api, onSelect, onSettle])

  // Focus viewport after React re-renders with updated aria-label
  React.useEffect(() => {
    if (!shouldFocusViewportRef.current || !api) return
    shouldFocusViewportRef.current = false
    const viewport = api.rootNode()
    if (viewport) {
      viewport.focus()
    }
  }, [settledSlidesInView, api])

  // Reset browser-native focus-scroll on the viewport
  React.useEffect(() => {
    if (!api) return
    const viewport = api.rootNode()
    if (!viewport) return
    const resetScroll = () => {
      viewport.scrollLeft = 0
      viewport.scrollTop = 0
    }
    viewport.addEventListener('scroll', resetScroll)
    return () => viewport.removeEventListener('scroll', resetScroll)
  }, [api])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        settledSlidesInView,
        totalSlides,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn('relative', className)}
        role='region'
        aria-roledescription='carousel'
        data-slot='carousel'
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({ className, children, ...props }: React.ComponentProps<'div'>) {
  const { carouselRef, orientation, settledSlidesInView, totalSlides } = useCarousel()
  const { t } = useTranslation()

  // Use toArray to filter out falsy children (from conditional rendering like
  // {condition && <Slide />}) BEFORE assigning indices. React.Children.map
  // counts false/null children in its index, creating gaps that mismatch
  // Embla's sequential DOM-based slide indices and breaking the inert system.
  const validChildren = React.Children.toArray(children)
  const indexedChildren = validChildren.map((child, index) => (
    <CarouselItemIndexContext.Provider key={(child as React.ReactElement).key ?? index} value={index}>
      {child}
    </CarouselItemIndexContext.Provider>
  ))

  const viewportAriaLabel =
    settledSlidesInView !== null && settledSlidesInView.length > 0 && totalSlides > 0
      ? t('carousel.showingItems', {
          start: settledSlidesInView[0] + 1,
          end: settledSlidesInView[settledSlidesInView.length - 1] + 1,
          total: totalSlides,
        })
      : totalSlides > 0
      ? t('carousel.content')
      : undefined

  return (
    <div
      ref={carouselRef}
      // overflow-hidden is the fallback; modern browsers use overflow: clip with
      // overflow-clip-margin so the offset focus outline of slide cards isn't clipped.
      // focus: (not just focus-visible:) ensures outline shows on programmatic .focus() calls,
      // which don't reliably trigger :focus-visible in all browsers.
      className='overflow-hidden supports-[overflow:clip]:overflow-clip [overflow-clip-margin:0.5rem] focus:outline-2 focus:outline-offset-2 focus:outline-(--accent-purple) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-purple)'
      data-slot='carousel-viewport'
      tabIndex={-1}
      aria-label={viewportAriaLabel}
    >
      <div className={cn('flex m-0 p-0', orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col', className)} {...props}>
        {indexedChildren}
      </div>
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation, settledSlidesInView, totalSlides } = useCarousel()
  const { t } = useTranslation()
  const resolvedIndex = useCarouselItemIndex()

  const isInert = resolvedIndex !== undefined && settledSlidesInView !== null && !settledSlidesInView.includes(resolvedIndex)

  return (
    <div
      role='group'
      aria-roledescription='slide'
      aria-label={
        resolvedIndex !== undefined && totalSlides > 0
          ? t('carousel.slideLabel', { current: resolvedIndex + 1, total: totalSlides })
          : undefined
      }
      data-slot='carousel-item'
      data-slide-index={resolvedIndex}
      className={cn('min-w-0 shrink-0 grow-0 basis-full', orientation === 'horizontal' ? 'pl-4' : 'pt-4', className)}
      inert={isInert ? true : undefined}
      {...props}
    />
  )
}

function CarouselPrevious({
  className,
  variant = 'outline',
  size = 'icon',
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()
  const { t } = useTranslation()

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!canScrollPrev) {
        e.preventDefault()
        return
      }
      scrollPrev()
      onClick?.(e)
    },
    [canScrollPrev, scrollPrev, onClick]
  )

  return (
    <Button
      data-slot='carousel-previous'
      variant={variant}
      size={size}
      className={cn(
        'absolute size-14 rounded-full bg-white border-2 border-(--background-dark) aria-disabled:opacity-30 aria-disabled:cursor-not-allowed',
        orientation === 'horizontal'
          ? 'top-1/2 -left-12 -translate-y-1/2'
          : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      aria-disabled={canScrollPrev ? undefined : 'true'}
      onClick={handleClick}
      {...props}
    >
      <ArrowLeft className='size-6 text-(--accent-purple)' aria-hidden='true' />
      <span className='sr-only'>{t('carousel.previousSlide')}</span>
    </Button>
  )
}

function CarouselNext({
  className,
  variant = 'outline',
  size = 'icon',
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel()
  const { t } = useTranslation()

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!canScrollNext) {
        e.preventDefault()
        return
      }
      scrollNext()
      onClick?.(e)
    },
    [canScrollNext, scrollNext, onClick]
  )

  return (
    <Button
      data-slot='carousel-next'
      variant={variant}
      size={size}
      className={cn(
        'absolute size-14 rounded-full bg-white border-2 border-(--background-dark) aria-disabled:opacity-30 aria-disabled:cursor-not-allowed',
        orientation === 'horizontal'
          ? 'top-1/2 -right-12 -translate-y-1/2'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      aria-disabled={canScrollNext ? undefined : 'true'}
      onClick={handleClick}
      {...props}
    >
      <ArrowRight className='size-6 text-(--accent-purple)' aria-hidden='true' />
      <span className='sr-only'>{t('carousel.nextSlide')}</span>
    </Button>
  )
}

export { type CarouselApi, Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext }
